import { liveReportingQueryKeys } from 'src/queries/liveReporting/liveReporting.keys'
import { reportingAgentQueryKeys } from 'src/queries/reportingAgent/reportingAgent.keys'

import { reportingErrorStatus } from './reporting.errors'

export interface ReportingPrivateCacheClient {
  removeQueries: (options: {
    queryKey: readonly unknown[]
    type?: 'inactive'
  }) => unknown
  cancelQueries?: (options: {
    queryKey: readonly unknown[]
    type?: 'inactive'
  }) => unknown
}

const scrubReportingPrivateCacheEntries = (
  queryClient: ReportingPrivateCacheClient,
  actorId: string | undefined,
  organizationId: string | undefined,
  inactiveOnly: boolean,
): void => {
  const roots = [
    reportingAgentQueryKeys.root(actorId, organizationId),
    liveReportingQueryKeys.root(actorId, organizationId),
  ]
  for (const queryKey of roots) {
    const options = {
      queryKey,
      ...(inactiveOnly ? { type: 'inactive' as const } : {}),
    }
    void queryClient.cancelQueries?.(options)
    queryClient.removeQueries({
      ...options,
    })
  }
}

/**
 * Conversation and live-report queries are both private. Scrubbing one family
 * alone can expose a cached row from the other after an auth, permission,
 * actor, or organization transition.
 */
export const scrubReportingPrivateCache = (
  queryClient: ReportingPrivateCacheClient,
  actorId: string | undefined,
  organizationId: string | undefined,
): void => {
  scrubReportingPrivateCacheEntries(queryClient, actorId, organizationId, false)
}

/** The auth reducer does not clear React Query, so logout must remove all
 * reporting scopes instead of relying on a Reporting view being mounted. */
export const scrubAllReportingPrivateCache = (
  queryClient: ReportingPrivateCacheClient,
): void => {
  for (const queryKey of [
    ['reportingAgent'] as const,
    ['liveReporting'] as const,
  ]) {
    void queryClient.cancelQueries?.({ queryKey })
    queryClient.removeQueries({ queryKey })
  }
}

/** A 401/403 immediately evicts inactive private data. Mounted queries remain
 * long enough to publish the error without triggering a remove/recreate loop. */
export const scrubUnauthorizedReportingPrivateCache = (
  queryClient: ReportingPrivateCacheClient,
  queryKey: readonly unknown[],
  error: unknown,
): void => {
  const status = reportingErrorStatus(error)
  if (status !== 401 && status !== 403) return
  if (
    (queryKey[0] !== 'reportingAgent' && queryKey[0] !== 'liveReporting') ||
    typeof queryKey[1] !== 'string' ||
    typeof queryKey[2] !== 'string'
  ) {
    return
  }
  scrubReportingPrivateCacheEntries(queryClient, queryKey[1], queryKey[2], true)
}
